import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, Package, TrendingUp, TrendingDown, ArrowUpDown, Store, Clock, IndianRupee, BarChart3, ShoppingCart, AlertTriangle, Download, RefreshCw, Zap, Settings, Tag, Search, Calendar, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── KPI CARD ───────────────────────────────────────────────
function KPI({ icon: Icon, label, value, sub, color }) {
  const colors = { blue: "bg-blue-50 text-blue-600", emerald: "bg-emerald-50 text-emerald-600", purple: "bg-purple-50 text-purple-600", amber: "bg-amber-50 text-amber-600" };
  return (
    <div className="bg-white rounded-xl border p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colors[color]}`}><Icon className="w-5 h-5" /></div>
      <div><p className="text-lg font-bold text-slate-800 leading-tight">{value}</p><p className="text-xs text-slate-500">{label}</p>{sub && <p className="text-xs text-slate-400 truncate max-w-[120px]">{sub}</p>}</div>
    </div>
  );
}

// ─── INPUT TAB ──────────────────────────────────────────────
function InputTab({ onUploadSuccess }) {
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [status, setStatus] = useState(null);

  useEffect(() => { axios.get(`${API}/ap/sales-analytics/status`).then(r => setStatus(r.data)).catch(() => {}); }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.name.match(/\.xlsx?$/i)) { toast.error("Only .xlsx files supported"); return; }
    setUploading(true); setResult(null);
    try {
      const form = new FormData(); form.append("file", file);
      const r = await axios.post(`${API}/ap/sales-analytics/upload`, form);
      setResult(r.data);
      toast.success(`Uploaded: ${r.data.stock?.new || 0} new stock + ${r.data.sales?.new || 0} new sales records`);
      onUploadSuccess?.();
      axios.get(`${API}/ap/sales-analytics/status`).then(r => setStatus(r.data)).catch(() => {});
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    setUploading(false); e.target.value = "";
  };

  return (
    <div className="space-y-6">
      <div className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center hover:border-slate-400 transition-colors bg-white">
        <FileSpreadsheet className="w-12 h-12 mx-auto mb-3 text-slate-400" />
        <p className="text-sm text-slate-600 mb-4">Upload AP Excel with STOCK and SALES sheets</p>
        <Button disabled={uploading} size="sm" onClick={() => document.getElementById("ap-sales-input").click()} data-testid="ap-upload-excel">
          <Upload className="w-4 h-4 mr-2" />{uploading ? "Processing..." : "Choose File"}
        </Button>
        <input id="ap-sales-input" type="file" accept=".xlsx,.xls" onChange={handleUpload} className="hidden" />
      </div>
      {result && (
        <div className="bg-white rounded-xl border p-5">
          <h3 className="font-semibold text-sm mb-3">Upload Summary</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-blue-50 rounded-lg p-3"><p className="text-xs text-blue-600 font-medium">Stock</p><p className="text-lg font-bold text-blue-800">{result.stock?.new || 0} new, {result.stock?.updated || 0} updated</p></div>
            <div className="bg-emerald-50 rounded-lg p-3"><p className="text-xs text-emerald-600 font-medium">Sales</p><p className="text-lg font-bold text-emerald-800">{result.sales?.new || 0} new, {result.sales?.skipped || 0} skipped</p></div>
          </div>
        </div>
      )}
      {status && (
        <div className="bg-white rounded-xl border p-5">
          <h3 className="font-semibold text-sm mb-3">Current AP Data</h3>
          <div className="grid grid-cols-2 gap-4">
            <div><p className="text-lg font-bold text-slate-800">{status.stock_records?.toLocaleString()}</p><p className="text-xs text-slate-500">Stock records</p></div>
            <div><p className="text-lg font-bold text-slate-800">{status.sales_records?.toLocaleString()}</p><p className="text-xs text-slate-500">Sales records</p></div>
          </div>
          {status.last_upload && <p className="text-xs text-slate-400 mt-3">Last upload: {status.last_upload.filename} — {new Date(status.last_upload.uploaded_at).toLocaleString()}</p>}
          <div className="mt-4 pt-3 border-t">
            <Button variant="outline" size="sm" onClick={() => window.open(`${API}/ap/sales-analytics/export`, "_blank")} data-testid="ap-export-data-btn">
              <Download className="w-4 h-4 mr-2" />Export All Data (Excel)
            </Button>
            <span className="text-xs text-slate-400 ml-3">Downloads STOCK + SALES sheets in the same format as uploaded</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── STOCK POSITION TAB ─────────────────────────────────────
function StockPositionTab() {
  const [summary, setSummary] = useState(null);
  const [slowMovers, setSlowMovers] = useState([]);
  const [byProduct, setByProduct] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState("total_value");
  const [sortDir, setSortDir] = useState(-1);
  const [selectedStore, setSelectedStore] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, slow, prod] = await Promise.all([
        axios.get(`${API}/ap/sales-analytics/stock/summary`),
        axios.get(`${API}/ap/sales-analytics/stock/slow-movers?min_age=60`),
        axios.get(`${API}/ap/sales-analytics/stock/by-product`),
      ]);
      setSummary(s.data); setSlowMovers(slow.data); setByProduct(prod.data);
    } catch { toast.error("Failed to load stock data"); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const storeParam = selectedStore ? `&store=${encodeURIComponent(selectedStore)}` : "";
    Promise.all([
      axios.get(`${API}/ap/sales-analytics/stock/slow-movers?min_age=60${storeParam}`),
      axios.get(`${API}/ap/sales-analytics/stock/by-product${selectedStore ? `?store=${encodeURIComponent(selectedStore)}` : ""}`),
    ]).then(([slow, prod]) => { setSlowMovers(slow.data); setByProduct(prod.data); }).catch(() => {});
  }, [selectedStore]);

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-600" /></div>;
  if (!summary || summary.total_units === 0) return <div className="text-center py-12 text-slate-400">No AP stock data. Upload an Excel file first.</div>;

  const sortedStores = [...(summary.stores || [])].sort((a, b) => (a[sortField] - b[sortField]) * sortDir);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI icon={Package} label="Total Stock" value={summary.total_units.toLocaleString()} sub="units" color="blue" />
        <KPI icon={IndianRupee} label="Stock Value" value={`${(summary.total_value / 100000).toFixed(1)}L`} sub={`Rs.${summary.total_value.toLocaleString()}`} color="emerald" />
        <KPI icon={Store} label="Stores" value={summary.store_count} sub="active" color="purple" />
        <KPI icon={AlertTriangle} label="Slow Movers" value={slowMovers.length} sub={selectedStore || "> 60 days old"} color="amber" />
      </div>

      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="px-5 py-3 border-b flex justify-between items-center">
          <h3 className="font-semibold text-slate-800 text-sm">Stock Position by Store</h3>
          {selectedStore && <button onClick={() => setSelectedStore(null)} className="text-xs text-blue-600 hover:text-blue-800 font-medium">Clear filter: {selectedStore}</button>}
        </div>
        <p className="px-5 py-1.5 text-xs text-slate-400">Click a store to filter slow movers and stock by product below</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
              <tr>
                <th className="px-4 py-2.5 text-left">Store</th><th className="px-4 py-2.5 text-left">Region</th>
                <th className="px-4 py-2.5 text-right cursor-pointer" onClick={() => { setSortField("total_units"); setSortDir(d => -d); }}>Units <ArrowUpDown className="w-3 h-3 inline ml-1" /></th>
                <th className="px-4 py-2.5 text-right cursor-pointer" onClick={() => { setSortField("total_value"); setSortDir(d => -d); }}>Value <ArrowUpDown className="w-3 h-3 inline ml-1" /></th>
                <th className="px-4 py-2.5 text-right">Products</th>
                <th className="px-4 py-2.5 text-right cursor-pointer" onClick={() => { setSortField("avg_age"); setSortDir(d => -d); }}>Avg Age <ArrowUpDown className="w-3 h-3 inline ml-1" /></th>
                <th className="px-4 py-2.5 text-right">Max Age</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {sortedStores.map((s, i) => (
                <tr key={i} className={`cursor-pointer transition-colors ${selectedStore === s.store ? "bg-blue-50 hover:bg-blue-100" : "hover:bg-slate-50"}`} onClick={() => setSelectedStore(prev => prev === s.store ? null : s.store)}>
                  <td className="px-4 py-2.5 font-medium text-slate-800">{selectedStore === s.store && <span className="text-blue-500 mr-1">&#9654;</span>}{s.store}</td>
                  <td className="px-4 py-2.5 text-slate-500">{s.region}</td>
                  <td className="px-4 py-2.5 text-right font-medium">{s.total_units}</td>
                  <td className="px-4 py-2.5 text-right">Rs.{s.total_value.toLocaleString()}</td>
                  <td className="px-4 py-2.5 text-right">{s.product_count}</td>
                  <td className="px-4 py-2.5 text-right"><span className={s.avg_age > 90 ? "text-red-600 font-medium" : s.avg_age > 60 ? "text-amber-600" : "text-emerald-600"}>{s.avg_age}d</span></td>
                  <td className="px-4 py-2.5 text-right text-slate-500">{s.max_age}d</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border overflow-hidden">
          <div className="px-5 py-3 border-b bg-amber-50"><h3 className="font-semibold text-amber-800 text-sm flex items-center gap-2"><TrendingDown className="w-4 h-4" /> Slow Movers (60+ days){selectedStore ? ` — ${selectedStore}` : ""}</h3></div>
          <div className="max-h-[400px] overflow-y-auto">
            {slowMovers.length === 0 ? <p className="text-center py-6 text-slate-400 text-sm">No slow movers{selectedStore ? ` in ${selectedStore}` : ""}</p> : (
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-500 uppercase sticky top-0"><tr><th className="px-3 py-2 text-left">Product</th>{!selectedStore && <th className="px-3 py-2 text-left">Store</th>}<th className="px-3 py-2 text-right">Age</th><th className="px-3 py-2 text-right">Qty</th><th className="px-3 py-2 text-right">Value</th></tr></thead>
                <tbody className="divide-y">
                  {slowMovers.slice(0, 50).map((item, i) => (
                    <tr key={i} className="hover:bg-amber-50/50">
                      <td className="px-3 py-1.5 font-medium text-slate-700">{item.product}</td>
                      {!selectedStore && <td className="px-3 py-1.5 text-slate-500">{item.store}</td>}
                      <td className="px-3 py-1.5 text-right"><span className={item.max_age > 120 ? "text-red-600 font-bold" : "text-amber-600 font-medium"}>{item.max_age}d</span></td>
                      <td className="px-3 py-1.5 text-right">{item.units}</td>
                      <td className="px-3 py-1.5 text-right text-slate-500">Rs.{item.total_value?.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl border overflow-hidden">
          <div className="px-5 py-3 border-b bg-blue-50"><h3 className="font-semibold text-blue-800 text-sm flex items-center gap-2"><Package className="w-4 h-4" /> Stock by Product{selectedStore ? ` — ${selectedStore}` : " (All Stores)"}</h3></div>
          <div className="max-h-[400px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-500 uppercase sticky top-0"><tr><th className="px-3 py-2 text-left">Product</th><th className="px-3 py-2 text-right">Units</th><th className="px-3 py-2 text-right">Value</th><th className="px-3 py-2 text-right">Avg Age</th>{!selectedStore && <th className="px-3 py-2 text-right">Stores</th>}</tr></thead>
              <tbody className="divide-y">
                {byProduct.map((p, i) => (
                  <tr key={i} className="hover:bg-blue-50/50">
                    <td className="px-3 py-1.5 font-medium text-slate-700">{p.product}</td>
                    <td className="px-3 py-1.5 text-right font-medium">{p.total_units}</td>
                    <td className="px-3 py-1.5 text-right">Rs.{p.total_value.toLocaleString()}</td>
                    <td className="px-3 py-1.5 text-right"><span className={p.avg_age > 90 ? "text-red-600" : p.avg_age > 60 ? "text-amber-600" : "text-emerald-600"}>{p.avg_age}d</span></td>
                    {!selectedStore && <td className="px-3 py-1.5 text-right">{p.store_count}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── SALES POSITION TAB ─────────────────────────────────────
function SalesPositionTab() {
  const [summary, setSummary] = useState(null);
  const [topProducts, setTopProducts] = useState([]);
  const [monthly, setMonthly] = useState([]);
  const [storeProducts, setStoreProducts] = useState([]);
  const [selectedStore, setSelectedStore] = useState("");
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("all");
  const [filterValue, setFilterValue] = useState("");
  const [periodsData, setPeriodsData] = useState({ months: [], quarters: [], years: [] });

  useEffect(() => { axios.get(`${API}/ap/sales-analytics/sales/periods`).then(r => setPeriodsData(r.data)).catch(() => {}); }, []);

  const getDateParams = useCallback(() => {
    if (filterType === "all" || !filterValue) return "";
    let date_from, date_to;
    if (filterType === "month") { const [y, m] = filterValue.split("-"); const lastDay = new Date(parseInt(y), parseInt(m), 0).getDate(); date_from = `${filterValue}-01`; date_to = `${filterValue}-${lastDay}`; }
    else if (filterType === "quarter") { const q = periodsData.quarters.find(q => q.label === filterValue); if (!q) return ""; date_from = q.from; date_to = q.to; }
    else if (filterType === "year") { date_from = `${filterValue}-01-01`; date_to = `${filterValue}-12-31`; }
    if (!date_from) return "";
    return `date_from=${date_from}&date_to=${date_to}`;
  }, [filterType, filterValue, periodsData.quarters]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const dp = getDateParams(); const qs = dp ? `?${dp}` : ""; const topQs = dp ? `?${dp}&limit=20` : "?limit=20";
      const [s, top, mo] = await Promise.all([
        axios.get(`${API}/ap/sales-analytics/sales/summary${qs}`),
        axios.get(`${API}/ap/sales-analytics/sales/top-products${topQs}`),
        axios.get(`${API}/ap/sales-analytics/sales/monthly${qs}`),
      ]);
      setSummary(s.data); setTopProducts(top.data); setMonthly(mo.data);
    } catch { toast.error("Failed to load sales data"); }
    setLoading(false);
  }, [getDateParams]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    if (!selectedStore) return;
    const dp = getDateParams(); const storeParam = `store=${encodeURIComponent(selectedStore)}`; const qs = dp ? `?${storeParam}&${dp}` : `?${storeParam}`;
    axios.get(`${API}/ap/sales-analytics/sales/by-store${qs}`).then(r => setStoreProducts(r.data)).catch(() => {});
  }, [selectedStore, getDateParams]);

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-600" /></div>;
  if (!summary || summary.total_quantity === 0) return <div className="text-center py-12 text-slate-400">No AP sales data{filterType !== "all" ? " for this period" : ""}.</div>;

  const monthNames = { "01": "Jan", "02": "Feb", "03": "Mar", "04": "Apr", "05": "May", "06": "Jun", "07": "Jul", "08": "Aug", "09": "Sep", "10": "Oct", "11": "Nov", "12": "Dec" };
  const maxMonthlyQty = Math.max(...monthly.map(m => m.total_qty), 1);
  const formatMonthLabel = (m) => { if (!m) return ""; const p = m.split("-"); return `${monthNames[p[1]] || p[1]} ${p[0]?.slice(2)}`; };

  return (
    <div className="space-y-6">
      {/* Date Filter Bar */}
      <div className="bg-white rounded-xl border p-4 flex flex-wrap items-center gap-3">
        <span className="text-xs font-semibold text-slate-500 uppercase">Period:</span>
        <div className="flex gap-1 bg-slate-100 rounded-lg p-0.5">
          {[{ id: "all", label: "All Time" }, { id: "month", label: "Month" }, { id: "quarter", label: "Quarter" }, { id: "year", label: "Year" }].map(t => (
            <button key={t.id} onClick={() => { setFilterType(t.id); setFilterValue(""); }}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${filterType === t.id ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>{t.label}</button>
          ))}
        </div>
        {filterType === "month" && periodsData.months.length > 0 && (
          <div className="flex gap-1 flex-wrap">{periodsData.months.map(m => (
            <button key={m} onClick={() => setFilterValue(m)} className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${filterValue === m ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{formatMonthLabel(m)}</button>
          ))}</div>
        )}
        {filterType === "quarter" && periodsData.quarters.length > 0 && (
          <div className="flex gap-1 flex-wrap">{periodsData.quarters.map(q => (
            <button key={q.label} onClick={() => setFilterValue(q.label)} className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${filterValue === q.label ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{q.label}</button>
          ))}</div>
        )}
        {filterType === "year" && periodsData.years.length > 0 && (
          <div className="flex gap-1 flex-wrap">{periodsData.years.map(y => (
            <button key={y} onClick={() => setFilterValue(y)} className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${filterValue === y ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{y}</button>
          ))}</div>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI icon={ShoppingCart} label="Total Sold" value={summary.total_quantity.toLocaleString()} sub="units" color="emerald" />
        <KPI icon={IndianRupee} label="Revenue" value={`${(summary.total_revenue / 100000).toFixed(1)}L`} sub={`Rs.${summary.total_revenue.toLocaleString()}`} color="blue" />
        <KPI icon={Store} label="Stores" value={summary.store_count} sub="selling" color="purple" />
        <KPI icon={TrendingUp} label="Top Product" value={topProducts[0]?.total_qty || 0} sub={topProducts[0]?.product || "-"} color="amber" />
      </div>

      <div className="bg-white rounded-xl border p-5">
        <h3 className="font-semibold text-slate-800 text-sm mb-4">Monthly Sales Trend</h3>
        <div className="flex items-end gap-3 h-40">
          {monthly.map((m, i) => {
            const height = (m.total_qty / maxMonthlyQty) * 100;
            return (<div key={i} className="flex-1 flex flex-col items-center gap-1"><span className="text-xs font-bold text-slate-700">{m.total_qty}</span><div className="w-full bg-blue-500 rounded-t-md transition-all" style={{ height: `${Math.max(height, 4)}%` }} /><span className="text-xs text-slate-500">{formatMonthLabel(m.month)}</span><span className="text-xs text-slate-400">Rs.{(m.total_revenue / 1000).toFixed(0)}k</span></div>);
          })}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border overflow-hidden">
          <div className="px-5 py-3 border-b bg-emerald-50"><h3 className="font-semibold text-emerald-800 text-sm flex items-center gap-2"><TrendingUp className="w-4 h-4" /> Top Selling Products</h3></div>
          <div className="max-h-[400px] overflow-y-auto">
            <table className="w-full text-xs"><thead className="bg-slate-50 text-slate-500 uppercase sticky top-0"><tr><th className="px-3 py-2 text-left">#</th><th className="px-3 py-2 text-left">Product</th><th className="px-3 py-2 text-right">Qty</th><th className="px-3 py-2 text-right">Revenue</th><th className="px-3 py-2 text-right">Stores</th></tr></thead>
            <tbody className="divide-y">{topProducts.map((p, i) => (
              <tr key={i} className="hover:bg-emerald-50/50"><td className="px-3 py-1.5 text-slate-400">{i + 1}</td><td className="px-3 py-1.5 font-medium text-slate-700">{p.product}</td><td className="px-3 py-1.5 text-right font-bold text-emerald-700">{p.total_qty}</td><td className="px-3 py-1.5 text-right">Rs.{p.total_revenue.toLocaleString()}</td><td className="px-3 py-1.5 text-right">{p.store_count}</td></tr>
            ))}</tbody></table>
          </div>
        </div>

        <div className="bg-white rounded-xl border overflow-hidden">
          <div className="px-5 py-3 border-b"><h3 className="font-semibold text-slate-800 text-sm flex items-center gap-2"><Store className="w-4 h-4" /> Sales by Store</h3><p className="text-xs text-slate-400 mt-0.5">Click a store to see product breakdown</p></div>
          <div className="max-h-[400px] overflow-y-auto">
            <table className="w-full text-xs"><thead className="bg-slate-50 text-slate-500 uppercase sticky top-0"><tr><th className="px-3 py-2 text-left">Store</th><th className="px-3 py-2 text-left">Region</th><th className="px-3 py-2 text-right">Qty</th><th className="px-3 py-2 text-right">Revenue</th></tr></thead>
            <tbody className="divide-y">{summary.stores.map((s, i) => (
              <tr key={i} className={`cursor-pointer transition-colors ${selectedStore === s.store ? "bg-blue-50 hover:bg-blue-100" : "hover:bg-slate-50"}`} onClick={() => setSelectedStore(prev => prev === s.store ? "" : s.store)}>
                <td className="px-3 py-1.5 font-medium text-slate-700">{selectedStore === s.store && <span className="text-blue-500 mr-1">&#9654;</span>}{s.store}</td><td className="px-3 py-1.5 text-slate-500">{s.region}</td><td className="px-3 py-1.5 text-right font-medium">{s.total_quantity}</td><td className="px-3 py-1.5 text-right">Rs.{s.total_revenue.toLocaleString()}</td>
              </tr>
            ))}</tbody></table>
          </div>
        </div>
      </div>

      {selectedStore && storeProducts.length > 0 && (
        <div className="bg-white rounded-xl border overflow-hidden">
          <div className="px-5 py-3 border-b flex justify-between items-center"><h3 className="font-semibold text-slate-800 text-sm">Product Sales — {selectedStore}</h3><button className="text-xs text-blue-600 hover:text-blue-800 font-medium" onClick={() => setSelectedStore("")}>Close</button></div>
          <div className="max-h-[300px] overflow-y-auto">
            <table className="w-full text-xs"><thead className="bg-slate-50 text-slate-500 uppercase sticky top-0"><tr><th className="px-3 py-2 text-left">Product</th><th className="px-3 py-2 text-right">Qty Sold</th><th className="px-3 py-2 text-right">Revenue</th><th className="px-3 py-2 text-right">First Sale</th><th className="px-3 py-2 text-right">Last Sale</th></tr></thead>
            <tbody className="divide-y">{storeProducts.map((p, i) => (
              <tr key={i} className="hover:bg-slate-50"><td className="px-3 py-1.5 font-medium text-slate-700">{p.product}</td><td className="px-3 py-1.5 text-right font-bold">{p.total_qty}</td><td className="px-3 py-1.5 text-right">Rs.{p.total_revenue.toLocaleString()}</td><td className="px-3 py-1.5 text-right text-slate-400">{p.first_sale}</td><td className="px-3 py-1.5 text-right text-slate-400">{p.last_sale}</td></tr>
            ))}</tbody></table>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── REORDER ANALYTICS TAB ──────────────────────────────────
function ReorderAnalyticsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/ap/sales-analytics/reorder`)
      .then(r => setData(r.data))
      .catch(() => toast.error("Failed to load reorder data"))
      .finally(() => setLoading(false));
  }, []);

  const handleDownload = () => {
    window.open(`${API}/ap/sales-analytics/reorder/download`, "_blank");
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-600" /></div>;
  if (!data || data.error) return <div className="text-center py-12 text-slate-400">{data?.error || "No reorder data available"}</div>;

  const { urgent = [], restock = [], skipped = [], summary = {} } = data;
  const allReorders = [...urgent, ...restock];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI icon={AlertTriangle} label="Urgent Re-orders" value={urgent.length} sub="low stock + selling" color="amber" />
        <KPI icon={Package} label="Restock Needed" value={restock.length} sub="out of stock at stores" color="blue" />
        <KPI icon={ShoppingCart} label="Total Suggested" value={summary.total_suggested_qty || 0} sub="units across all stores" color="emerald" />
        <KPI icon={Store} label="Stores" value={summary.stores_needing_reorder || 0} sub="needing stock" color="purple" />
      </div>

      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={handleDownload} data-testid="ap-download-reorder"><Download className="w-4 h-4 mr-1.5" />Download Excel</Button>
      </div>

      {urgent.length > 0 && (
        <div className="bg-white rounded-xl border overflow-hidden">
          <div className="px-5 py-3 border-b bg-amber-50"><h3 className="font-semibold text-amber-800 text-sm flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Urgent — Low Stock + Active Sales ({urgent.length})</h3></div>
          <div className="max-h-[400px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-500 uppercase sticky top-0"><tr>
                <th className="px-3 py-2 text-left">Store</th><th className="px-3 py-2 text-left">Product</th>
                <th className="px-3 py-2 text-right">Current Stock</th><th className="px-3 py-2 text-right">Monthly Sales</th>
                <th className="px-3 py-2 text-right">Suggested Qty</th><th className="px-3 py-2 text-right">MRP</th>
                <th className="px-3 py-2 text-center">HQ Stock</th>
              </tr></thead>
              <tbody className="divide-y">{urgent.map((r, i) => (
                <tr key={i} className="hover:bg-amber-50/50">
                  <td className="px-3 py-1.5 font-medium text-slate-700">{r.store}</td>
                  <td className="px-3 py-1.5 text-slate-700">{r.product}</td>
                  <td className="px-3 py-1.5 text-right"><span className="text-red-600 font-bold">{r.current_stock}</span></td>
                  <td className="px-3 py-1.5 text-right font-medium">{r.monthly_velocity}/mo</td>
                  <td className="px-3 py-1.5 text-right font-bold text-blue-700">{r.suggested_qty}</td>
                  <td className="px-3 py-1.5 text-right text-slate-500">Rs.{r.mrp?.toLocaleString()}</td>
                  <td className="px-3 py-1.5 text-center">{r.hq_available != null ? <Badge className={r.hq_available > 0 ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}>{r.hq_available}</Badge> : "-"}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </div>
      )}

      {restock.length > 0 && (
        <div className="bg-white rounded-xl border overflow-hidden">
          <div className="px-5 py-3 border-b bg-blue-50"><h3 className="font-semibold text-blue-800 text-sm flex items-center gap-2"><Package className="w-4 h-4" /> Restock — Out of Stock at Store ({restock.length})</h3></div>
          <div className="max-h-[400px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-500 uppercase sticky top-0"><tr>
                <th className="px-3 py-2 text-left">Store</th><th className="px-3 py-2 text-left">Product</th>
                <th className="px-3 py-2 text-right">Monthly Sales</th><th className="px-3 py-2 text-right">Suggested Qty</th>
                <th className="px-3 py-2 text-right">MRP</th><th className="px-3 py-2 text-center">HQ Stock</th>
              </tr></thead>
              <tbody className="divide-y">{restock.map((r, i) => (
                <tr key={i} className="hover:bg-blue-50/50">
                  <td className="px-3 py-1.5 font-medium text-slate-700">{r.store}</td>
                  <td className="px-3 py-1.5 text-slate-700">{r.product}</td>
                  <td className="px-3 py-1.5 text-right font-medium">{r.monthly_velocity}/mo</td>
                  <td className="px-3 py-1.5 text-right font-bold text-blue-700">{r.suggested_qty}</td>
                  <td className="px-3 py-1.5 text-right text-slate-500">Rs.{r.mrp?.toLocaleString()}</td>
                  <td className="px-3 py-1.5 text-center">{r.hq_available != null ? <Badge className={r.hq_available > 0 ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}>{r.hq_available}</Badge> : "-"}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </div>
      )}

      {allReorders.length === 0 && <div className="text-center py-12 text-slate-400">All stores are well stocked. No reorders needed.</div>}
    </div>
  );
}


// ─── REORDER LOGIC TAB ──────────────────────────────────────
function ReorderLogicTab() {
  const [settings, setSettings] = useState(null);
  const [stockMonths, setStockMonths] = useState(1);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/ap/sales-analytics/reorder/settings`)
      .then(r => { setSettings(r.data); setStockMonths(r.data.stock_months || 1); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/ap/sales-analytics/reorder/settings`, { stock_months: stockMonths });
      setSettings(r.data);
      toast.success("AP reorder logic updated");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save"); }
    setSaving(false);
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-600" /></div>;

  const presets = [
    { label: "1 Month", value: 1, desc: "Match monthly consumption exactly" },
    { label: "1.5 Months", value: 1.5, desc: "50% buffer above monthly sales" },
    { label: "2 Months", value: 2, desc: "Double the monthly consumption" },
    { label: "3 Months", value: 3, desc: "Quarter stock, aggressive growth" },
  ];

  const examples = [
    { vel: 1, stock: 1 }, { vel: 1.2, stock: 1 }, { vel: 3, stock: 2 }, { vel: 5.5, stock: 0 },
  ];

  return (
    <div className="space-y-6 max-w-3xl">
      <div><p className="text-sm text-slate-500">Configure how many months of stock each AP store should maintain. This determines the suggested re-order quantity for every SKU.</p></div>

      <div className="bg-white rounded-xl border p-5 space-y-4">
        <h3 className="font-semibold text-slate-800 text-sm">Stock Months Multiplier</h3>
        <p className="text-xs text-slate-500">Formula: <span className="font-mono bg-slate-100 px-1.5 py-0.5 rounded">Suggested Qty = max(ceil(Monthly Sales x Multiplier) - Current Stock, 0)</span></p>

        <div className="flex flex-wrap gap-2" data-testid="ap-reorder-presets">
          {presets.map(p => (
            <button key={p.value} onClick={() => setStockMonths(p.value)}
              className={`px-3 py-2 rounded-lg text-xs font-medium border transition-all ${stockMonths === p.value ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>
              {p.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <label className="text-xs font-medium text-slate-600">Custom value:</label>
          <input type="number" min="0.5" max="12" step="0.5" value={stockMonths} onChange={e => setStockMonths(parseFloat(e.target.value) || 1)}
            className="w-24 px-3 py-1.5 border rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-300" data-testid="ap-stock-months-input" />
          <span className="text-xs text-slate-400">months (0.5 — 12)</span>
        </div>

        <div className="flex items-center gap-3">
          <Button onClick={handleSave} disabled={saving} variant="default" size="sm" data-testid="ap-save-reorder-settings">
            {saving ? "Saving..." : "Save Setting"}
          </Button>
          {settings?.updated_at && <span className="text-xs text-slate-400">Last updated: {new Date(settings.updated_at).toLocaleString()}</span>}
        </div>
      </div>

      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="px-5 py-3 border-b bg-slate-50"><h3 className="font-semibold text-slate-700 text-sm">Preview — {stockMonths}x Monthly Sales</h3></div>
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-slate-500 uppercase text-[11px]"><tr><th className="px-4 py-2 text-right">Monthly Sales</th><th className="px-4 py-2 text-right">Current Stock</th><th className="px-4 py-2 text-right">Suggested Re-order</th></tr></thead>
          <tbody className="divide-y">{examples.map((ex, i) => {
            const suggested = Math.max(Math.ceil(ex.vel * stockMonths) - ex.stock, 0);
            return (<tr key={i} className="hover:bg-slate-50"><td className="px-4 py-2 text-right font-medium">{ex.vel}/mo</td><td className="px-4 py-2 text-right">{ex.stock}</td><td className="px-4 py-2 text-right font-bold text-blue-700">{suggested}</td></tr>);
          })}</tbody>
        </table>
      </div>

      <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-500">
        <p>Changes apply immediately to the Re-order Analytics tab and Excel downloads. Higher multipliers mean more stock in hand — useful during growth phases.</p>
      </div>
    </div>
  );
}


// ─── SKU ANALYTICS TAB (AP) ─────────────────────────────────
function SKUAnalyticsTab({ apiBase }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortBy, setSortBy] = useState("total_qty");
  const [sortDir, setSortDir] = useState("desc");
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedSku, setExpandedSku] = useState(null);

  const loadData = useCallback(async (from, to) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (from) params.set("date_from", from);
      if (to) params.set("date_to", to);
      const r = await axios.get(`${apiBase}/sku-analytics?${params}`);
      setData(r.data);
    } catch { toast.error("Failed to load SKU analytics"); }
    setLoading(false);
  }, [apiBase]);

  useEffect(() => { loadData(dateFrom, dateTo); }, [loadData, dateFrom, dateTo]);

  const handleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === "desc" ? "asc" : "desc");
    else { setSortBy(col); setSortDir("desc"); }
  };

  const filtered = React.useMemo(() => {
    if (!data?.skus) return [];
    let items = data.skus;
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      items = items.filter(i => i.product.toLowerCase().includes(s));
    }
    items.sort((a, b) => {
      const av = a[sortBy] ?? 0, bv = b[sortBy] ?? 0;
      if (typeof av === "string") return sortDir === "desc" ? bv.localeCompare(av) : av.localeCompare(bv);
      return sortDir === "desc" ? bv - av : av - bv;
    });
    return items;
  }, [data, searchTerm, sortBy, sortDir]);

  const fmtINR = (v) => `₹${(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
  const SortHead = ({ col, label }) => (
    <TableHead className="cursor-pointer hover:text-slate-800 select-none" onClick={() => handleSort(col)}>
      <span className="flex items-center gap-1">{label}{sortBy === col && <span className="text-blue-500">{sortDir === "desc" ? "↓" : "↑"}</span>}</span>
    </TableHead>
  );
  const urgencyBadge = (u) => {
    if (u === "restock_now") return <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-100 text-red-700">RESTOCK NOW</span>;
    if (u === "plan_restock") return <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-700">PLAN RESTOCK</span>;
    return <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-700">OK</span>;
  };

  if (loading) return <div className="text-center py-12 text-slate-400">Loading SKU analytics...</div>;

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border p-4">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-slate-400" />
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="border rounded-md px-2 py-1.5 text-xs h-8" />
            <span className="text-slate-400 text-xs">to</span>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="border rounded-md px-2 py-1.5 text-xs h-8" />
          </div>
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-2 text-slate-400" />
            <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full border rounded-md pl-8 pr-3 py-1.5 text-xs h-8" placeholder="Search SKU..." />
          </div>
          <div className="text-xs text-slate-400">{filtered.length} SKUs</div>
        </div>
      </div>
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: "Total SKUs", value: filtered.length, color: "text-slate-800" },
          { label: "Units Sold", value: filtered.reduce((s, i) => s + i.total_qty, 0), color: "text-blue-700" },
          { label: "Revenue", value: fmtINR(filtered.reduce((s, i) => s + i.total_revenue, 0)), color: "text-emerald-700" },
          { label: "Need Restock", value: filtered.filter(i => i.restock_urgency !== "ok").length, color: "text-red-700" },
        ].map((c, i) => (
          <div key={i} className="bg-white rounded-xl border p-4">
            <p className="text-xs text-slate-500">{c.label}</p>
            <p className={`text-xl font-bold ${c.color}`}>{c.value}</p>
          </div>
        ))}
      </div>
      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead className="w-8"></TableHead>
              <SortHead col="product" label="SKU" />
              <SortHead col="total_qty" label="Units" />
              <SortHead col="total_revenue" label="Revenue" />
              <SortHead col="mrp" label="MRP" />
              <SortHead col="store_count" label="Stores" />
              <SortHead col="warehouse_stock" label="Our Stock" />
              <SortHead col="store_stock" label="Store Stock" />
              <SortHead col="daily_rate" label="Daily Rate" />
              <SortHead col="days_of_stock" label="Days Left" />
              <TableHead>Restock</TableHead>
              <SortHead col="suggested_reorder" label="Reorder" />
            </TableRow></TableHeader>
            <TableBody>{filtered.map(sku => (
              <React.Fragment key={sku.product}>
                <TableRow className={`hover:bg-slate-50 ${expandedSku === sku.product ? "bg-blue-50/50" : ""}`}>
                  <TableCell><button onClick={() => setExpandedSku(expandedSku === sku.product ? null : sku.product)} className="text-slate-400 hover:text-slate-600">
                    {expandedSku === sku.product ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                  </button></TableCell>
                  <TableCell className="font-medium text-sm">{sku.product}</TableCell>
                  <TableCell className="font-bold">{sku.total_qty}</TableCell>
                  <TableCell className="text-emerald-700">{fmtINR(sku.total_revenue)}</TableCell>
                  <TableCell className="text-xs text-slate-500">{fmtINR(sku.mrp)}</TableCell>
                  <TableCell className="text-xs">{sku.store_count}</TableCell>
                  <TableCell className={`font-bold ${sku.warehouse_stock <= 0 ? "text-red-600" : sku.warehouse_stock < 5 ? "text-amber-600" : ""}`}>{sku.warehouse_stock}</TableCell>
                  <TableCell className="text-xs text-slate-500">{sku.store_stock}</TableCell>
                  <TableCell className="text-xs">{sku.daily_rate}/day</TableCell>
                  <TableCell className={`font-bold ${sku.days_of_stock <= 90 ? "text-red-600" : sku.days_of_stock <= 135 ? "text-amber-600" : "text-emerald-600"}`}>
                    {sku.days_of_stock >= 999 ? "∞" : `${sku.days_of_stock}d`}
                  </TableCell>
                  <TableCell>{urgencyBadge(sku.restock_urgency)}</TableCell>
                  <TableCell className="font-bold text-blue-700">{sku.suggested_reorder > 0 ? sku.suggested_reorder : "-"}</TableCell>
                </TableRow>
                {expandedSku === sku.product && (
                  <TableRow><TableCell colSpan={11} className="bg-slate-50 p-0">
                    <div className="px-6 py-3">
                      <p className="text-xs font-bold text-slate-500 mb-2">Monthly — {sku.product}</p>
                      <div className="grid grid-cols-6 gap-2">
                        {(data?.months || []).map(m => {
                          const md = sku.monthly?.[m] || {};
                          const mn = {"01":"Jan","02":"Feb","03":"Mar","04":"Apr","05":"May","06":"Jun","07":"Jul","08":"Aug","09":"Sep","10":"Oct","11":"Nov","12":"Dec"};
                          const p = m.split("-");
                          return (<div key={m} className={`rounded-lg border p-2.5 text-center ${md.qty ? "bg-white" : "bg-slate-100 opacity-50"}`}>
                            <p className="text-[10px] font-bold text-slate-400 uppercase">{mn[p[1]]||p[1]} {p[0]?.slice(2)}</p>
                            <p className="text-lg font-bold">{md.qty || 0}</p>
                            <p className="text-[10px] text-emerald-600">{md.revenue ? fmtINR(md.revenue) : "-"}</p>
                          </div>);
                        })}
                      </div>
                      <div className="flex gap-4 mt-2 text-xs text-slate-400">
                        <span>First sale: {sku.first_sale || "-"}</span>
                        <span>Last sale: {sku.last_sale || "-"}</span>
                        <span>Txns: {sku.txn_count}</span>
                      </div>
                    </div>
                  </TableCell></TableRow>
                )}
              </React.Fragment>
            ))}</TableBody>
          </Table>
        </div>
        {filtered.length === 0 && <div className="text-center py-10 text-slate-400 text-sm">No SKU data found.</div>}
      </div>
    </div>
  );
}



// ─── COMMISSION INVOICE TAB ─────────────────────────────────
const COMMISSION_LS_KEY = "ap_commission_invoice_settings";

const DEFAULT_COMMISSION_SETTINGS = {
  ourCompanyName: "", ourAddr1: "", ourAddr2: "", ourCity: "", ourPhone: "", ourEmail: "", ourGST: "", ourIEC: "",
  billedToName: "", billedToAddr1: "", billedToAddr2: "", billedToCity: "", billedToPhone: "", billedToEmail: "", billedToGST: "",
  invPrefix: "CI-AP-", invCounter: 1,
  productName: "Sales Commission Services", hsn: "998313", commissionRate: 5,
};

function CommissionInvoiceTab() {
  const [settings, setSettings] = useState(() => {
    try { return { ...DEFAULT_COMMISSION_SETTINGS, ...JSON.parse(localStorage.getItem(COMMISSION_LS_KEY)) }; }
    catch { return { ...DEFAULT_COMMISSION_SETTINGS }; }
  });
  const [showSettings, setShowSettings] = useState(false);
  const [monthly, setMonthly] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    axios.get(`${API}/ap/sales-analytics/sales/monthly`).then(r => { setMonthly(r.data || []); }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const saveSettings = (updated) => { setSettings(updated); localStorage.setItem(COMMISSION_LS_KEY, JSON.stringify(updated)); };
  const updateField = (field, value) => { const updated = { ...settings, [field]: value }; saveSettings(updated); };

  const monthNames = { "01": "Jan", "02": "Feb", "03": "Mar", "04": "Apr", "05": "May", "06": "Jun", "07": "Jul", "08": "Aug", "09": "Sep", "10": "Oct", "11": "Nov", "12": "Dec" };
  const formatMonthLabel = (m) => { if (!m) return ""; const p = m.split("-"); return `${monthNames[p[1]] || p[1]} ${p[0]?.slice(2)}`; };

  const selectedData = monthly.find(m => m.month === selectedMonth);
  const revenue = selectedData?.total_revenue || 0;
  const rate = (settings.commissionRate || 5) / 100;
  const unitPrice = revenue * rate;
  const gst = unitPrice * 0.18;
  const grandTotal = unitPrice + gst;
  const invNumber = `${settings.invPrefix}${String(settings.invCounter).padStart(3, "0")}`;

  const incrementCounter = () => {
    const updated = { ...settings, invCounter: settings.invCounter + 1 };
    saveSettings(updated);
  };

  const handleDownloadPDF = async () => {
    if (!selectedMonth) { toast.error("Select a month first"); return; }
    setGenerating(true);
    try {
      const { default: html2canvas } = await import("html2canvas");
      const { default: jsPDF } = await import("jspdf");
      const el = document.getElementById("commission-invoice-preview");
      const canvas = await html2canvas(el, { scale: 2, useCORS: true, backgroundColor: "#fff" });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfW = pdf.internal.pageSize.getWidth();
      const pdfH = pdf.internal.pageSize.getHeight();
      pdf.addImage(imgData, "PNG", 0, 0, pdfW, pdfH);
      pdf.save(`${invNumber}.pdf`);
      incrementCounter();
      toast.success("PDF downloaded");
    } catch (e) { toast.error("PDF generation failed"); console.error(e); }
    setGenerating(false);
  };

  const handleDownloadExcel = async () => {
    if (!selectedMonth) { toast.error("Select a month first"); return; }
    try {
      const XLSX = await import("xlsx");
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet([]);

      // Helper to set cell with style-like formatting
      const setCell = (r, c, val) => {
        const addr = XLSX.utils.encode_cell({ r, c });
        ws[addr] = { v: val, t: typeof val === "number" ? "n" : "s" };
      };

      let row = 0;

      // Company header
      setCell(row, 0, settings.ourCompanyName || "Your Company");
      ws["!merges"] = ws["!merges"] || [];
      ws["!merges"].push({ s: { r: row, c: 0 }, e: { r: row, c: 5 } });
      row++;
      setCell(row, 0, [settings.ourAddr1, settings.ourAddr2, settings.ourCity].filter(Boolean).join(" | ") +
        (settings.ourGST ? ` | GSTIN: ${settings.ourGST}` : "") +
        (settings.ourIEC ? ` | IEC: ${settings.ourIEC}` : ""));
      ws["!merges"].push({ s: { r: row, c: 0 }, e: { r: row, c: 5 } });
      row += 2;

      // Title
      setCell(row, 0, "COMMERCIAL INVOICE");
      ws["!merges"].push({ s: { r: row, c: 0 }, e: { r: row, c: 2 } });
      setCell(row, 3, "Invoice No.");
      setCell(row, 4, invNumber);
      row++;
      setCell(row, 3, "Date");
      setCell(row, 4, new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }));
      row++;
      setCell(row, 3, "Period");
      setCell(row, 4, formatMonthLabel(selectedMonth));
      row += 2;

      // FROM section
      setCell(row, 0, "FROM");
      setCell(row, 3, "BILLED TO");
      row++;
      setCell(row, 0, settings.ourCompanyName || "");
      setCell(row, 3, settings.billedToName || "");
      row++;
      const fromLines = [settings.ourAddr1, settings.ourAddr2, settings.ourCity, settings.ourPhone, settings.ourEmail].filter(Boolean);
      const toLines = [settings.billedToAddr1, settings.billedToAddr2, settings.billedToCity,
        settings.billedToGST ? `GSTIN: ${settings.billedToGST}` : "", settings.billedToPhone].filter(Boolean);
      const maxLines = Math.max(fromLines.length, toLines.length);
      for (let i = 0; i < maxLines; i++) {
        if (fromLines[i]) setCell(row, 0, fromLines[i]);
        if (toLines[i]) setCell(row, 3, toLines[i]);
        row++;
      }
      row++;

      // Product table header
      const headers = ["SR.", "DESCRIPTION", "HSN", "QTY", "UNIT PRICE", "AMOUNT"];
      headers.forEach((h, c) => setCell(row, c, h));
      row++;

      // Product row
      setCell(row, 0, 1);
      setCell(row, 1, `${settings.productName || "Sales Commission"} — Commission @ ${settings.commissionRate || 5}% on ${formatMonthLabel(selectedMonth)} revenue of ₹${revenue.toLocaleString("en-IN")}`);
      setCell(row, 2, settings.hsn || "998313");
      setCell(row, 3, 1);
      setCell(row, 4, Math.round(unitPrice * 100) / 100);
      setCell(row, 5, Math.round(unitPrice * 100) / 100);
      row += 2;

      // Totals
      setCell(row, 4, "Subtotal");
      setCell(row, 5, Math.round(unitPrice * 100) / 100);
      row++;
      setCell(row, 4, "GST @ 18%");
      setCell(row, 5, Math.round(gst * 100) / 100);
      row++;
      setCell(row, 4, "GRAND TOTAL");
      setCell(row, 5, Math.round(grandTotal * 100) / 100);
      row += 2;

      // Footer
      setCell(row, 0, "This is a computer generated invoice and does not require a signature or stamp.");
      ws["!merges"].push({ s: { r: row, c: 0 }, e: { r: row, c: 5 } });

      // Column widths
      ws["!cols"] = [
        { wch: 6 }, { wch: 50 }, { wch: 10 }, { wch: 8 }, { wch: 16 }, { wch: 16 }
      ];

      // Set the range
      ws["!ref"] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: row, c: 5 } });

      XLSX.utils.book_append_sheet(wb, ws, "Commission Invoice");
      XLSX.writeFile(wb, `${invNumber}.xlsx`);
      incrementCounter();
      toast.success("Excel downloaded");
    } catch (e) { toast.error("Excel generation failed"); console.error(e); }
  };

  const fmtINR = (v) => v.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const SettingsField = ({ label, field, type = "text", placeholder }) => (
    <div>
      <label className="text-xs font-medium text-slate-500 block mb-1">{label}</label>
      <Input type={type} value={settings[field] || ""} onChange={e => updateField(field, type === "number" ? parseFloat(e.target.value) || 0 : e.target.value)}
        placeholder={placeholder} className="h-8 text-sm" />
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Settings Toggle */}
      <div className="bg-white rounded-xl border">
        <button onClick={() => setShowSettings(!showSettings)} className="w-full px-5 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors rounded-xl">
          <span className="font-semibold text-slate-800 text-sm flex items-center gap-2"><Settings className="w-4 h-4" /> Invoice Settings</span>
          <span className="text-xs text-slate-400">{showSettings ? "▲ Collapse" : "▼ Expand"}</span>
        </button>
        {showSettings && (
          <div className="px-5 pb-5 pt-2 border-t space-y-5">
            <div>
              <h4 className="text-xs font-bold text-blue-600 uppercase tracking-wide mb-3">Our Company</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <SettingsField label="Company Name" field="ourCompanyName" placeholder="Your company name" />
                <SettingsField label="Address Line 1" field="ourAddr1" placeholder="Street address" />
                <SettingsField label="Address Line 2" field="ourAddr2" placeholder="Area / Locality" />
                <SettingsField label="City / State / PIN" field="ourCity" placeholder="Mumbai, MH 400001" />
                <SettingsField label="Phone" field="ourPhone" placeholder="+91..." />
                <SettingsField label="Email" field="ourEmail" placeholder="billing@company.com" />
                <SettingsField label="GSTIN" field="ourGST" placeholder="27XXXXX1234X1Z5" />
                <SettingsField label="IEC Code" field="ourIEC" placeholder="0300019564" />
              </div>
            </div>
            <div>
              <h4 className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-3">Billed To</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <SettingsField label="Customer Name" field="billedToName" placeholder="Customer company" />
                <SettingsField label="Address Line 1" field="billedToAddr1" placeholder="Street address" />
                <SettingsField label="Address Line 2" field="billedToAddr2" placeholder="Area / Locality" />
                <SettingsField label="City / State / PIN" field="billedToCity" placeholder="City, State PIN" />
                <SettingsField label="Phone" field="billedToPhone" placeholder="+91..." />
                <SettingsField label="Email" field="billedToEmail" placeholder="accounts@customer.com" />
                <SettingsField label="GSTIN" field="billedToGST" placeholder="27XXXXX1234X1Z5" />
              </div>
            </div>
            <div>
              <h4 className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-3">Invoice & Product</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <SettingsField label="Invoice Prefix" field="invPrefix" placeholder="CI-AP-" />
                <SettingsField label="Starting Number" field="invCounter" type="number" placeholder="1" />
                <SettingsField label="Product Name" field="productName" placeholder="Sales Commission Services" />
                <SettingsField label="HSN Code" field="hsn" placeholder="998313" />
                <SettingsField label="Commission Rate %" field="commissionRate" type="number" placeholder="5" />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Month Selector */}
      <div className="bg-white rounded-xl border p-5">
        <h3 className="font-semibold text-slate-800 text-sm mb-3">Select Month</h3>
        {loading ? <div className="text-center py-4 text-slate-400 text-sm">Loading...</div> :
          monthly.length === 0 ? <div className="text-center py-4 text-slate-400 text-sm">No sales data available. Upload data in the Input tab first.</div> :
          <div className="flex gap-2 flex-wrap">
            {monthly.map(m => (
              <button key={m.month} onClick={() => setSelectedMonth(m.month)}
                className={`px-4 py-2.5 rounded-lg text-sm font-medium border transition-all ${
                  selectedMonth === m.month ? "bg-blue-600 text-white border-blue-600 shadow-sm" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                }`}>
                <div>{formatMonthLabel(m.month)}</div>
                <div className={`text-xs mt-0.5 ${selectedMonth === m.month ? "text-blue-200" : "text-slate-400"}`}>
                  ₹{(m.total_revenue / 100000).toFixed(1)}L
                </div>
              </button>
            ))}
          </div>
        }
      </div>

      {/* Invoice Preview + Actions */}
      {selectedMonth && revenue > 0 && (
        <>
          <div className="flex gap-3 items-center">
            <Button onClick={handleDownloadPDF} disabled={generating} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="commission-download-pdf">
              <Download className="w-4 h-4 mr-2" />{generating ? "Generating..." : "Download PDF"}
            </Button>
            <Button onClick={handleDownloadExcel} variant="outline" data-testid="commission-download-excel">
              <FileSpreadsheet className="w-4 h-4 mr-2" />Download Excel
            </Button>
            <span className="text-xs text-slate-400">Invoice #{invNumber} • Counter auto-increments on download</span>
          </div>

          {/* Invoice Preview */}
          <div className="bg-white rounded-xl border p-2 overflow-auto">
            <div id="commission-invoice-preview" style={{
              width: "210mm", minHeight: "297mm", margin: "0 auto",
              fontFamily: "Arial, sans-serif", fontSize: "9pt",
              background: "#fff", color: "#1c1c1e",
              padding: "12mm 14mm", boxSizing: "border-box"
            }}>
              {/* Header bar */}
              <div style={{ background: "#003a8c", color: "#fff", padding: "10px 16px", borderRadius: "6px", marginBottom: "12px" }}>
                <div style={{ fontSize: "16pt", fontWeight: "bold" }}>{settings.ourCompanyName || "Your Company"}</div>
                <div style={{ fontSize: "8pt", opacity: 0.8, marginTop: "2px" }}>
                  {[settings.ourAddr1, settings.ourAddr2, settings.ourCity].filter(Boolean).join(" | ")}
                  {settings.ourGST ? ` | GSTIN: ${settings.ourGST}` : ""}
                  {settings.ourIEC ? ` | IEC: ${settings.ourIEC}` : ""}
                </div>
              </div>

              {/* Title + meta */}
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "14px", alignItems: "flex-start" }}>
                <div style={{ fontSize: "13pt", fontWeight: "bold", letterSpacing: "1px" }}>COMMERCIAL INVOICE</div>
                <div style={{ textAlign: "right", fontSize: "8pt", color: "#444" }}>
                  <div><b>Invoice No.</b> {invNumber}</div>
                  <div><b>Date</b> {new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</div>
                  <div><b>Period</b> {formatMonthLabel(selectedMonth)}</div>
                </div>
              </div>

              {/* FROM / TO */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "16px" }}>
                <div style={{ background: "#f5f5f7", borderRadius: "6px", padding: "10px 12px" }}>
                  <div style={{ fontSize: "7pt", fontWeight: "bold", color: "#007aff", marginBottom: "5px", letterSpacing: "0.5px" }}>FROM</div>
                  <div style={{ fontWeight: "bold", fontSize: "10pt" }}>{settings.ourCompanyName || "Your Company"}</div>
                  {[settings.ourAddr1, settings.ourAddr2, settings.ourCity].filter(Boolean).map((l, i) => (
                    <div key={i} style={{ fontSize: "8pt", color: "#444" }}>{l}</div>
                  ))}
                  {settings.ourPhone && <div style={{ fontSize: "8pt", color: "#444" }}>{settings.ourPhone}</div>}
                  {settings.ourEmail && <div style={{ fontSize: "8pt", color: "#444" }}>{settings.ourEmail}</div>}
                </div>
                <div style={{ background: "#f5f5f7", borderRadius: "6px", padding: "10px 12px" }}>
                  <div style={{ fontSize: "7pt", fontWeight: "bold", color: "#333", marginBottom: "5px", letterSpacing: "0.5px" }}>BILLED TO</div>
                  <div style={{ fontWeight: "bold", fontSize: "10pt" }}>{settings.billedToName || "Customer"}</div>
                  {[settings.billedToAddr1, settings.billedToAddr2, settings.billedToCity].filter(Boolean).map((l, i) => (
                    <div key={i} style={{ fontSize: "8pt", color: "#444" }}>{l}</div>
                  ))}
                  {settings.billedToGST && <div style={{ fontSize: "8pt", color: "#444" }}>GSTIN: {settings.billedToGST}</div>}
                  {settings.billedToPhone && <div style={{ fontSize: "8pt", color: "#444" }}>{settings.billedToPhone}</div>}
                </div>
              </div>

              {/* Product table */}
              <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "0" }}>
                <thead>
                  <tr style={{ background: "#1c1c1e", color: "#fff" }}>
                    {["SR.", "DESCRIPTION", "HSN", "QTY", "UNIT PRICE", "AMOUNT"].map(h => (
                      <th key={h} style={{ padding: "8px 10px", textAlign: h === "SR." || h === "QTY" ? "center" : "left", fontSize: "8pt" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ background: "#fafafa" }}>
                    <td style={{ padding: "10px", textAlign: "center", borderBottom: "1px solid #eee" }}>1</td>
                    <td style={{ padding: "10px", borderBottom: "1px solid #eee" }}>
                      <div style={{ fontWeight: "500" }}>{settings.productName || "Sales Commission"}</div>
                      <div style={{ fontSize: "7.5pt", color: "#666" }}>Commission @ {settings.commissionRate || 5}% on {formatMonthLabel(selectedMonth)} revenue of ₹{revenue.toLocaleString("en-IN")}</div>
                    </td>
                    <td style={{ padding: "10px", borderBottom: "1px solid #eee", color: "#666" }}>{settings.hsn || "998313"}</td>
                    <td style={{ padding: "10px", textAlign: "center", borderBottom: "1px solid #eee" }}>1</td>
                    <td style={{ padding: "10px", borderBottom: "1px solid #eee" }}>₹{fmtINR(unitPrice)}</td>
                    <td style={{ padding: "10px", borderBottom: "1px solid #eee", fontWeight: "500" }}>₹{fmtINR(unitPrice)}</td>
                  </tr>
                </tbody>
              </table>

              {/* Totals */}
              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "16px" }}>
                <table style={{ width: "260px", borderCollapse: "collapse" }}>
                  <tbody>
                    {[["Subtotal", unitPrice], ["GST @ 18%", gst]].map(([label, val]) => (
                      <tr key={label}>
                        <td style={{ padding: "5px 10px", color: "#555", fontSize: "8.5pt" }}>{label}</td>
                        <td style={{ padding: "5px 10px", textAlign: "right", fontSize: "8.5pt" }}>₹{fmtINR(val)}</td>
                      </tr>
                    ))}
                    <tr style={{ background: "#1c1c1e", color: "#fff" }}>
                      <td style={{ padding: "8px 10px", fontWeight: "bold" }}>GRAND TOTAL</td>
                      <td style={{ padding: "8px 10px", textAlign: "right", fontWeight: "bold" }}>₹{fmtINR(grandTotal)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Footer */}
              <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: "10px", fontSize: "7.5pt", color: "#888", textAlign: "center" }}>
                This is a computer generated invoice and does not require a signature or stamp.
              </div>
            </div>
          </div>
        </>
      )}

      {selectedMonth && revenue === 0 && (
        <div className="bg-white rounded-xl border p-10 text-center text-slate-400">
          <p>No revenue data for {formatMonthLabel(selectedMonth)}.</p>
        </div>
      )}
    </div>
  );
}


// ─── MAIN PAGE ──────────────────────────────────────────────
export default function APSalesAnalytics() {
  const [mainTab, setMainTab] = useState("input");
  const [outputTab, setOutputTab] = useState("stock");
  const [refreshKey, setRefreshKey] = useState(0);

  return (
    <div className="p-4 md:p-6 max-w-[1400px] mx-auto" data-testid="ap-sales-analytics-page">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2"><BarChart3 className="w-5 h-5" /> AP Sales Analytics</h1>
        <p className="text-sm text-slate-500 mt-0.5">Upload AP store data and analyze stock & sales performance</p>
      </div>

      <div className="flex gap-1 mb-4 bg-slate-100 rounded-lg p-1 w-fit">
        {[{ id: "input", label: "Input", icon: Upload }, { id: "output", label: "Output", icon: BarChart3 }, { id: "commission", label: "Commission Invoice", icon: IndianRupee }].map(t => (
          <button key={t.id} onClick={() => setMainTab(t.id)} data-testid={`ap-tab-${t.id}`}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-colors ${mainTab === t.id ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
            <t.icon className="w-4 h-4" />{t.label}
          </button>
        ))}
      </div>

      {mainTab === "input" && <InputTab onUploadSuccess={() => setRefreshKey(k => k + 1)} />}

      {mainTab === "commission" && <CommissionInvoiceTab />}

      {mainTab === "output" && (
        <div>
          <div className="flex gap-1 mb-4 bg-slate-100 rounded-lg p-1 w-fit">
            {[
              { id: "stock", label: "Stock Position", icon: Package },
              { id: "sales", label: "Sales Position", icon: ShoppingCart },
              { id: "reorder", label: "Re-order Analytics", icon: Zap },
              { id: "sku", label: "SKU Analytics", icon: Tag },
              { id: "logic", label: "Reorder Logic", icon: Settings },
            ].map(t => (
              <button key={t.id} onClick={() => setOutputTab(t.id)} data-testid={`ap-output-tab-${t.id}`}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-colors ${outputTab === t.id ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
                <t.icon className="w-4 h-4" />{t.label}
              </button>
            ))}
          </div>
          {outputTab === "stock" && <StockPositionTab key={`stock-${refreshKey}`} />}
          {outputTab === "sales" && <SalesPositionTab key={`sales-${refreshKey}`} />}
          {outputTab === "reorder" && <ReorderAnalyticsTab key={`reorder-${refreshKey}`} />}
          {outputTab === "sku" && <SKUAnalyticsTab key={`sku-${refreshKey}`} apiBase={`${API}/ap/sales-analytics`} />}
          {outputTab === "logic" && <ReorderLogicTab />}
        </div>
      )}
    </div>
  );
}
